import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { SCREEN_SHARE_TRACK_PREFIX } from '@/constants/devices';
import { call } from '@/services/call';
import { addChatMessage } from '@/store/features/chat/slice';
import { addParticipant, addParticipantInfo, removeParticipant, toggleParticipantMedia, toggleRoomPinned } from '@/store/features/room/slice';
import { useAppDispatch } from '@/store/hooks';
import { DataTrackMessage, DataTrackMessageType } from '@/types/call';
import { VideoPreview } from '@/ui';
import { showChatToast } from '@/utils/toast';
import RemoteParticipantContent from '../Content';
import { StyledParticipant } from './styled';

type Props = {
  sid: string;
};

const RemoteParticipant: FC<Props> = ({ sid }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const dispatch = useAppDispatch();

  const onNewMessage = useCallback((res: DataTrackMessage) => {
    switch (res.type) {
      case DataTrackMessageType.chat: {
        showChatToast(res.data.sender, res.data.text);
        dispatch(addChatMessage(res.data));
        break;
      }
      case DataTrackMessageType.userData: {
        dispatch(
          addParticipantInfo({
            sid: res.data.sender,
            user: {
              name: res.data.name || 'Guest',
              avatar: res.data.avatar || null,
            },
          }),
        );
        break;
      }
      default:
        break;
    }
  }, [dispatch]);

  const isScreenSharing = useMemo(() => sid === SCREEN_SHARE_TRACK_PREFIX, [sid]);

  useEffect(() => {
    if (!isScreenSharing) {
      const handleTrackSubscribed = (kind: string, enabled = true) => {
        dispatch(
          toggleParticipantMedia({
            kind,
            sid,
            enabled,
          })
        );
      };

      const handleTrackUnsubscribed = (kind: string, isScreen?: boolean) => {
        if (isScreen) {
          dispatch(
            removeParticipant(SCREEN_SHARE_TRACK_PREFIX),
          );
        } else {
          dispatch(
            toggleParticipantMedia({
              kind,
              sid,
              enabled: false,
            }),
          );
        }
      };

      call.onParticipantTrackSubscribed(
        {
          sid,
          videoWrapperElement: videoRef.current,
          audioCb(audioElement) {
            audioRef.current = audioElement;
          },
          screenCb() {
            dispatch(
              addParticipant({
                isAudioEnabled: false,
                isVideoEnabled: true,
                sid: SCREEN_SHARE_TRACK_PREFIX,
                identity: SCREEN_SHARE_TRACK_PREFIX,
              })
            );
            dispatch(
              toggleRoomPinned(SCREEN_SHARE_TRACK_PREFIX),
            );
          },
          dataCb: onNewMessage,
          cb: handleTrackSubscribed
        },
      );

      call.onParticipantTrackUnsubscribed(sid, handleTrackUnsubscribed);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.remove();
        audioRef.current = null;
      }
    };
  }, [dispatch, onNewMessage, sid, isScreenSharing]);

  return (
    <StyledParticipant>
      <VideoPreview id={isScreenSharing ? call.screenShareElementId : undefined} ref={videoRef}>
        <RemoteParticipantContent isScreenSharing={isScreenSharing} sid={sid} />
      </VideoPreview>
    </StyledParticipant>
  );
};

export default memo(RemoteParticipant);
